import { useState, useEffect, useMemo, useCallback } from 'react';
import { useGroups } from '../../api/useGroups';
import { useShareInvoice } from '../../api/useShareInvoice';
import { GroupItem } from '../../api/types';
import { extractString } from '../../utils/sanitizeData';

interface GroupDisplayItem {
  id: string;
  name: string;
  company?: string;
  displayLabel: string;
}

interface GroupSharingCardProps {
  publicToken?: string;
  initialGroups?: Array<string | number | { id?: string | number; name?: string; group_name?: string; company?: string; organization_name?: string }>;
}

// Extract human readable name for a group
function extractGroupName(g: GroupItem | Record<string, unknown> | string | number): string {
  if (typeof g === 'string') return g;
  if (typeof g === 'number') return `Group ${g}`;
  const obj = g as Record<string, unknown>;
  const raw = obj.name || obj.group_name || obj.title || obj.label || obj.id;
  return extractString(raw, 'Group');
}

// Extract company or organization affiliation for a group
function extractGroupCompany(g: GroupItem | Record<string, unknown> | string | number): string {
  if (typeof g === 'string' || typeof g === 'number') return '';
  const obj = g as Record<string, unknown>;
  const orgObj = typeof obj.organization === 'object' && obj.organization !== null ? (obj.organization as Record<string, unknown>) : null;
  const compObj = typeof obj.company === 'object' && obj.company !== null ? (obj.company as Record<string, unknown>) : null;

  const candidate =
    extractString(obj.company_name) ||
    extractString(obj.organization_name) ||
    extractString(compObj?.name) ||
    extractString(orgObj?.name) ||
    extractString(obj.company) ||
    extractString(obj.organization) ||
    extractString(obj.tenant_name) ||
    extractString(obj.description);

  return candidate;
}

export default function GroupSharingCard({
  publicToken,
  initialGroups = [],
}: GroupSharingCardProps) {
  const { data: orgGroups, isLoading: groupsLoading } = useGroups();
  const shareInvoiceMutation = useShareInvoice();

  // Normalize available groups from useGroups()
  const availableGroups = useMemo<GroupDisplayItem[]>(() => {
    if (orgGroups && Array.isArray(orgGroups) && orgGroups.length > 0) {
      return orgGroups.map((g, idx) => {
        const id = String(g.id || g.uuid || `group-${idx}`);
        const name = extractGroupName(g);
        const company = extractGroupCompany(g);
        const displayLabel = company ? `${name} (${company})` : name;
        return { id, name, company, displayLabel };
      });
    }
    return [
      { id: '1', name: 'Contabilidade', company: 'Autworld', displayLabel: 'Contabilidade (Autworld)' },
      { id: '2', name: 'Contabilidade', company: 'Holding', displayLabel: 'Contabilidade (Holding)' },
      { id: '3', name: 'Diretoria', company: 'Autworld', displayLabel: 'Diretoria (Autworld)' },
      { id: '4', name: 'Financeiro', company: 'Autworld', displayLabel: 'Financeiro (Autworld)' },
    ];
  }, [orgGroups]);

  // Helper to resolve an initial group item to a GroupDisplayItem
  const resolveToDisplayItem = useCallback(
    (raw: string | number | { id?: string | number; name?: string; group_name?: string }): GroupDisplayItem => {
      if (!raw) return { id: '', name: '', displayLabel: '' };

      let rawId = '';
      let rawName = '';

      if (typeof raw === 'string' || typeof raw === 'number') {
        rawId = String(raw);
        rawName = String(raw);
      } else if (typeof raw === 'object' && raw !== null) {
        rawId = String(raw.id || '');
        rawName = extractGroupName(raw);
      }

      // Match against available groups by ID or Name
      const matched = availableGroups.find(
        (g) => (rawId && g.id === rawId) || (rawName && g.name.toLowerCase() === rawName.toLowerCase())
      );

      if (matched) {
        return matched;
      }

      const company = extractGroupCompany(raw);
      const displayLabel = company ? `${rawName} (${company})` : rawName;
      return {
        id: rawId || rawName,
        name: rawName,
        company,
        displayLabel,
      };
    },
    [availableGroups]
  );

  const [activeGroups, setActiveGroups] = useState<GroupDisplayItem[]>(() =>
    initialGroups.map(resolveToDisplayItem).filter((g) => Boolean(g.id || g.name))
  );
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (initialGroups && Array.isArray(initialGroups) && initialGroups.length > 0) {
      setActiveGroups(initialGroups.map(resolveToDisplayItem).filter((g) => Boolean(g.id || g.name)));
    }
  }, [initialGroups, resolveToDisplayItem]);

  // Compute selectable groups by excluding already active group IDs
  const activeIds = activeGroups.map((g) => g.id);
  const selectableGroups = availableGroups.filter(
    (g) => !activeIds.includes(g.id)
  );

  const handleShare = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGroupId) return;

    const groupToAdd = availableGroups.find((g) => g.id === selectedGroupId);
    if (!groupToAdd) return;

    const newGroups = [...activeGroups, groupToAdd];
    setActiveGroups(newGroups);
    setSelectedGroupId('');

    if (publicToken) {
      setIsUpdating(true);
      try {
        await shareInvoiceMutation.mutateAsync({
          publicToken,
          payload: { shared_with: newGroups.map((g) => g.id) },
        });
      } catch {
        // Fallback in dev/demo
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const handleRevoke = async (groupId: string) => {
    const newGroups = activeGroups.filter((g) => g.id !== groupId);
    setActiveGroups(newGroups);

    if (publicToken) {
      setIsUpdating(true);
      try {
        await shareInvoiceMutation.mutateAsync({
          publicToken,
          payload: { shared_with: newGroups.map((g) => g.id) },
        });
      } catch {
        // Fallback in dev/demo
      } finally {
        setIsUpdating(false);
      }
    }
  };

  return (
    <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-5 shadow-card">
      <div className="flex items-center justify-between gap-2 mb-3 pb-3 border-b border-brand-line dark:border-strokedark">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-brand-vib fill-current" viewBox="0 0 20 20">
            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
          </svg>
          <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-brand-navy dark:text-white">
            Group Sharing
          </h3>
        </div>

        {isUpdating && (
          <span className="text-[10px] font-mono text-brand-soft animate-pulse">
            Syncing…
          </span>
        )}
      </div>

      {/* Active Shares List */}
      <div className="space-y-2 mb-4">
        {activeGroups.length > 0 ? (
          activeGroups.map((group) => (
            <div
              key={group.id || group.name}
              className="flex items-center justify-between p-2.5 rounded-xl bg-brand-bg dark:bg-strokedark/50 border border-brand-line dark:border-strokedark text-xs font-mono"
            >
              <div className="flex items-center gap-2 truncate">
                <svg className="w-4 h-4 text-brand-vib fill-current" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold text-brand-navy dark:text-white truncate">
                  {group.name}
                </span>
                {group.company && (
                  <span className="text-[10px] font-mono text-brand-soft dark:text-bodydark2 px-1.5 py-0.5 rounded bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark">
                    {group.company}
                  </span>
                )}
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-brand-green-tint text-[#0a6b4a] dark:bg-emerald-950/40 dark:text-emerald-300">
                  SHARED
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleRevoke(group.id)}
                className="text-brand-soft hover:text-rose-600 dark:hover:text-rose-400 p-1 transition-colors cursor-pointer"
                title="Revoke access"
              >
                ✕
              </button>
            </div>
          ))
        ) : (
          <p className="text-xs font-mono text-brand-soft dark:text-bodydark2 italic py-1">
            Not shared with any organizational groups.
          </p>
        )}
      </div>

      {/* Share with new group selector */}
      <form onSubmit={handleShare} className="flex gap-2">
        <select
          value={selectedGroupId}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          disabled={groupsLoading || selectableGroups.length === 0}
          className="w-full text-xs font-mono bg-white dark:bg-form-input border border-brand-line dark:border-form-strokedark rounded-xl px-3 py-2 text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib shadow-xs cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
        >
          <option value="">
            {groupsLoading
              ? 'Loading groups…'
              : selectableGroups.length === 0
              ? activeGroups.length > 0
                ? 'All available groups have access'
                : 'No sharing groups configured'
              : 'Select a group…'}
          </option>
          {selectableGroups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.displayLabel}
            </option>
          ))}
        </select>

        <button
          type="submit"
          disabled={!selectedGroupId || isUpdating}
          className="px-4 py-2 text-xs font-semibold bg-brand-vib text-white rounded-xl hover:bg-brand-vib-hover disabled:opacity-40 transition-colors shadow-sm whitespace-nowrap cursor-pointer disabled:cursor-not-allowed"
        >
          {isUpdating ? 'Sharing…' : 'Share'}
        </button>
      </form>
    </div>
  );
}
